import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { useToast } from '@/hooks/use-toast'
import { Loader2 } from 'lucide-react'

const knowledgeBaseSchema = z.object({
  question: z.string().min(5, 'Question must be at least 5 characters').max(500),
  answer: z.string().min(10, 'Answer must be at least 10 characters').max(2000),
  category: z.string().max(50).optional().or(z.literal('')),
  is_active: z.boolean(),
})

type KnowledgeBaseFormData = z.infer<typeof knowledgeBaseSchema>

interface KnowledgeBaseDialogProps {
  entryId: string | null
  accountId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function KnowledgeBaseDialog({
  entryId,
  accountId,
  open,
  onOpenChange,
}: KnowledgeBaseDialogProps) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const isEditing = !!entryId

  // Fetch entry if editing
  const { data: entry } = useQuery({
    queryKey: ['knowledge-base-entry', entryId],
    queryFn: async () => {
      if (!entryId) return null
      const { data, error } = await supabase
        .from('chat_knowledge_base')
        .select('*')
        .eq('id', entryId)
        .single()
      if (error) throw error
      return data
    },
    enabled: !!entryId && open,
  })

  const form = useForm<KnowledgeBaseFormData>({
    resolver: zodResolver(knowledgeBaseSchema),
    defaultValues: {
      question: '',
      answer: '',
      category: '',
      is_active: true,
    },
  })

  // Reset form when entry loads or dialog opens for new entry
  useEffect(() => {
    if (entry) {
      form.reset({
        question: entry.question,
        answer: entry.answer,
        category: entry.category || '',
        is_active: entry.is_active,
      })
    } else if (!entryId && open) {
      form.reset({
        question: '',
        answer: '',
        category: '',
        is_active: true,
      })
    }
  }, [entry, entryId, open, form])

  const mutation = useMutation({
    mutationFn: async (data: KnowledgeBaseFormData) => {
      if (isEditing && entryId) {
        const { error } = await supabase
          .from('chat_knowledge_base')
          .update({
            question: data.question,
            answer: data.answer,
            category: data.category || null,
            is_active: data.is_active,
          })
          .eq('id', entryId)
        if (error) throw error
      } else {
        // Get max display_order for new entries
        const { data: maxOrderData } = await supabase
          .from('chat_knowledge_base')
          .select('display_order')
          .eq('account_id', accountId)
          .order('display_order', { ascending: false })
          .limit(1)
          .single()

        const nextOrder = (maxOrderData?.display_order ?? -1) + 1

        const { error } = await supabase.from('chat_knowledge_base').insert({
          account_id: accountId,
          question: data.question,
          answer: data.answer,
          category: data.category || null,
          is_active: data.is_active,
          display_order: nextOrder,
        })
        if (error) throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge-base'] })
      toast({
        title: isEditing ? 'Entry updated' : 'Entry created',
        description: isEditing
          ? 'The knowledge base entry has been updated.'
          : 'A new knowledge base entry has been created.',
      })
      onOpenChange(false)
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit Knowledge Base Entry' : 'Add Knowledge Base Entry'}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update this Q&A pair for the AI assistant.'
              : 'Create a new Q&A pair to help the AI answer specific questions.'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit((data) => mutation.mutate(data))}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="question"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Question</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="What question should this answer?"
                      className="resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    The question or topic this entry addresses ({field.value?.length || 0}/500)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="answer"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Answer</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="The answer or information to provide..."
                      className="min-h-[120px] resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    The answer the AI should provide. Supports markdown formatting ({field.value?.length || 0}/2000)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Category (Optional)</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g., Pricing, Services, Policies"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Group related entries for organization
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <FormLabel>Active</FormLabel>
                    <FormDescription>
                      Include this entry in AI context
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {isEditing ? 'Save Changes' : 'Create Entry'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
